import { useState } from 'react';
import { ApiError } from '@/lib/api';
import { supportSessionDenial } from '@/lib/support-session';
import type { User } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { Button, Field, Modal, Textarea } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * Starting a SUPPORT SESSION — acting as another person for thirty minutes, to
 * reproduce what they see.
 *
 * ONE dialog for TWO entry points: the row action on `/users` and the search on
 * `/settings`. The RULES it enforces live in `@/lib/support-session`, which
 * both pages also read for their own gating, so nothing here and nothing there
 * can come to different conclusions about who may act as whom.
 *
 * ## Where the wire is
 *
 * **Nothing in this file touches it.** The whole of the transport is
 * `useAuth().startSupportSession(userId, reason)`, and that method is the thing
 * to read before changing anything here — it swaps the acting identity,
 * refuses to nest, and resolves only once the TARGET's identity and permissions
 * have loaded, which is why this dialog can close on the resolved promise
 * without a frame of the wrong person's access rendering under the right
 * person's name.
 *
 * ⚠️ **Never call `api.startSupportSession` from a page.** It is the raw POST:
 * it mints a support token and hands it back, and does not attach it, store it,
 * or reload the identity. Calling it directly starts a real session on the
 * server that this app is not in — a 201 with nothing on screen, which reads as
 * a dead button and leaves a live session nobody is holding.
 */

/*
 * The refusal this dialog recognises, matched on `error.code` and never on
 * message text — the house rule on every endpoint here.
 *
 * ✅ **Confirmed against the shipped contract, 2026-08-30**, read from
 * `support-sessions.service.ts` in `emerge-backend-app`: `422`, with
 * `details: [{ path: "userId", … }]`.
 *
 * ⚠️ **THERE IS NO SEPARATE "that is your own account" CODE, and this file used
 * to declare one.** `SUPPORT_SESSION_TARGET_IS_SELF` was a guess and does not
 * exist: the server refuses every `super_admin` target, **the caller's own
 * account included**, so the self case is FOLDED INTO the refusal below — the
 * caller is necessarily a super administrator to have reached the endpoint at
 * all. Its sentence names the ROLE rather than saying "another", precisely so
 * it reads correctly either way.
 *
 * That is why the branch handling it is gone rather than renamed. A dead
 * `error.code` match is invisible — nothing fails, nothing logs, `tsc` is
 * green, and the only evidence is a refusal that never arrives — so the
 * self-selection would have fallen through to the generic branch and lost its
 * pointer, while a reader of this file would have gone on believing the case
 * was handled.
 *
 * An unrecognised code still falls through to the server's own sentence,
 * exactly the way `quoteRefusalOf` degrades in `@/lib/xero`: unknown means
 * *newer than this branch*, never *not a refusal*.
 *
 * It earns a constant because it routes somewhere the generic refusal does not:
 * it says *the rule forbids this pairing, permanently*, which is not the
 * reader's to fix by pressing again.
 */
const TARGET_IS_SUPER_ADMIN_CODE = 'SUPPORT_SESSION_TARGET_IS_SUPER_ADMIN';

interface Failure {
  /** The SERVER's sentence, verbatim. Never paraphrased, never replaced. */
  message: string;
  /** `details[].reason` — why, where the server sent one. Rendered BESIDE the above. */
  reasons: string[];
  /** Bound to the reason box, when the server named that key. */
  fieldError: string | null;
  /** Ours: where the reader has to go. `null` when we have nothing to add. */
  note: string | null;
}

export interface SupportSessionDialogProps {
  /** The person to act as, or `null` to keep the dialog closed. */
  user: User | null;
  onClose: () => void;
  /** Fired once the session is live and the target's identity has loaded. */
  onStarted?: (user: User) => void;
}

/**
 * Collect the required reason, name the person, state the consequence, start.
 *
 * ⚠️ **The draft resets by REMOUNTING on the target, not by an effect that
 * reaches in and clears it.** A new person is a new decision — the typed reason
 * described somebody else, and a refusal on screen was about somebody else — so
 * `key={user.id}` on the form below makes that structural. An effect watching
 * `user` would do the same thing one render later, which is a frame of the
 * previous reason under the new person's name, and is the cascading-render
 * pattern the linter flags.
 */
export function SupportSessionDialog({ user, onClose, onStarted }: SupportSessionDialogProps) {
  /*
   * Closed renders nothing, which is what `Modal` does with `open={false}`
   * anyway — so there is no transition to preserve and nothing to keep mounted.
   * An unmounted dialog also cannot be reopened by a stale bit of state.
   */
  if (user === null) return null;
  const props: SupportSessionFormProps = onStarted
    ? { user, onClose, onStarted }
    : { user, onClose };
  return <SupportSessionForm key={user.id} {...props} />;
}

interface SupportSessionFormProps {
  user: User;
  onClose: () => void;
  onStarted?: (user: User) => void;
}

/**
 * ⚠️ **A `Modal` rather than a `ConfirmDialog`, and it keeps that component's
 * conventions rather than replacing them.** `ConfirmDialog` takes no input, so
 * the reason would need a dialog of its own and the confirmation a second one —
 * which puts the consequence on a screen the reader has already agreed to move
 * past. Kept from it: the backdrop is inert so a mis-click cannot dismiss the
 * decision, the body NAMES the person and states what actually happens, and the
 * confirm button carries the verb rather than "OK".
 *
 * Focus lands on the reason box, which is the one departure. `ConfirmDialog`
 * puts it on Cancel so a stray Enter hits the safe option; here nothing can
 * commit until a reason is typed, and the box is the work.
 */
function SupportSessionForm({ user, onClose, onStarted }: SupportSessionFormProps) {
  const { ability, user: actor, supportSession, startSupportSession } = useAuth();

  const [reason, setReason] = useState('');
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  /** Set when the server has refused in a way pressing again cannot change. */
  const [terminal, setTerminal] = useState(false);

  const denial = supportSessionDenial(ability, actor, user, supportSession);
  const trimmedReason = reason.trim();

  function start() {
    if (starting || trimmedReason === '' || denial !== null || terminal) return;
    setStarting(true);
    setFailure(null);
    /*
     * Resolves only once the target's identity and permissions have loaded, so
     * closing here cannot leave the reader looking at the super admin's own
     * access under somebody else's name.
     */
    startSupportSession(user.id, trimmedReason)
      .then(() => {
        onStarted?.(user);
        onClose();
      })
      .catch((caught: unknown) => {
        if (!(caught instanceof ApiError)) {
          setFailure({
            message:
              caught instanceof Error ? caught.message : 'Could not start the support session.',
            reasons: [],
            fieldError: null,
            note: null,
          });
          return;
        }
        /*
         * ⚠️ **The rule the controls already express, met from the server.**
         * Reaching it means the world moved under the reader — a role changed
         * under a live token, or somebody was promoted between the render and
         * the click. Held down afterwards (`terminal`), because pressing again
         * cannot produce a different answer and a button whose only outcome is
         * a repeated refusal is one nobody should be able to press twice.
         *
         * ⚠️ **This single branch covers picking your OWN row too**, since the
         * server folds that case in here rather than giving it a code — see the
         * constant. The note is therefore worded to be true either way: it does
         * not claim the screen was surprised, because on the self case it was
         * not, and it does not name the person, because on the self case that
         * is the reader.
         */
        if (caught.code === TARGET_IS_SUPER_ADMIN_CODE) {
          setTerminal(true);
          setFailure({
            message: caught.message,
            reasons: caught.detailReasons,
            /*
             * The 422 names `userId` in `details`, and this form renders no
             * such control — the target came from the row that opened the
             * dialog. Binding it to the reason box would put a sentence about
             * somebody's role under an input asking why. It goes in the banner.
             */
            fieldError: null,
            note: 'Nothing was started. A super administrator cannot be entered this way.',
          });
          return;
        }
        if (caught.status === 403) {
          setTerminal(true);
          setFailure({
            message: caught.message,
            reasons: caught.detailReasons,
            /*
             * A 403 carries no `details`, so it is a banner and never a field
             * message. It also means the gate above was an optimisation rather
             * than a guarantee — which is the state it exists to survive.
             */
            fieldError: null,
            note: 'Nothing was started.',
          });
          return;
        }
        if (caught.status === 404) {
          setTerminal(true);
          setFailure({
            message: caught.message,
            reasons: caught.detailReasons,
            fieldError: null,
            /*
             * ⚠️ **This note used to read "this API has no support-session
             * route", and that sentence has to go with the guessed path.** The
             * route exists; the only thing a 404 can mean here now is that
             * there is no such user — the service's own `findById` miss. The
             * old wording would have sent somebody to check a deployment over a
             * record that had simply been deleted, and it would have looked
             * entirely authoritative doing it.
             */
            note: 'Nothing was started. That account no longer exists.',
          });
          return;
        }
        /*
         * Ordered by SPECIFICITY — `fieldErrors` before `formMessage` before
         * `message` — never by which shape arrived first. A better-shaped error
         * landing on an endpoint already handled is a breaking change, and
         * reading `formMessage` first silently prefers the vaguer sentence the
         * day the server improves.
         *
         * `reason` is the only key this form renders, so anything else the
         * server names is unroutable and belongs in the banner, where it can be
         * read, rather than on an input that does not exist.
         */
        const fieldError = caught.fieldErrors['reason'] ?? null;
        setFailure({
          message: fieldError ?? caught.formMessage ?? caught.message,
          reasons: caught.detailReasons,
          fieldError,
          note: null,
        });
      })
      .finally(() => setStarting(false));
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Act as ${user.name}?`}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={starting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={start}
            loading={starting}
            disabled={denial !== null || trimmedReason === '' || terminal}
            title={denial ?? undefined}
          >
            Start support session
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/*
          The consequence, naming the person — `ConfirmDialog`'s own convention,
          and the reason this survives the no-helper-text prohibition: its
          exception list keeps body copy naming a real consequence by name.

          ⚠️ **Neither softened nor overstated.** It does not promise the
          session is invisible, and it does not describe it as a takeover. Two
          facts, both true: whose screen you get, and that the trail carries
          both names.

          ⚠️ **"thirty minutes" is the one figure here the SERVER owns and this
          sentence hardcodes**, and it is stated because before the session
          exists there is no fact to ship instead. The moment one starts there
          is: `SupportSessionResult.expiresAt`, carried on
          `useAuth().supportSession`. Any sentence about the window rendered
          AFTER the start must read that field — a sentence driven by a server
          value cannot drift from the server, one driven by a client's belief
          about it always can.
        */}
        <p className="m-0 text-sm text-fg-secondary">
          SEED EMERGE will act as{' '}
          <span className="font-medium text-fg">
            {user.name}
            {user.email ? ` (${user.email})` : ''}
          </span>{' '}
          for thirty minutes. Everything you do is recorded against both of you.
        </p>

        <Field label="Why you need this" required error={failure?.fieldError ?? undefined}>
          {(fieldProps) => (
            <Textarea
              {...fieldProps}
              value={reason}
              invalid={failure?.fieldError != null}
              disabled={denial !== null || starting}
              autoGrow={{ minRows: 2, maxRows: 5 }}
              onChange={(event) => {
                setReason(event.target.value);
                // A previous refusal described a previous reason.
                if (failure && !terminal) setFailure(null);
              }}
            />
          )}
        </Field>

        {/*
          Disabled-state text, not helper text: without it a held-down button
          makes the super-admin rule, an already-running session, your own row
          and an ordinary one look identical. It is text as well as a `title`
          because a disabled button has `pointer-events: none`, so its tooltip
          never reaches the person it is written for.
        */}
        {denial && <p className="m-0 text-xs text-fg-subtle">{denial}</p>}

        {/*
          ⚠️ **THREE halves render when all three exist, in this order**, and
          none may stand in for another — `message ?? reason` and
          `message ?? note` are each the silent drop this codebase has written
          up four times:

          - `message` — the server's own sentence, WHAT happened;
          - `reasons` — the server's own diagnosis, WHY. Verbatim;
          - `note` — ours, where the reader has to go. Last, because the
            server's words outrank ours and must not be pushed down.

          Empty `reasons` renders nothing at all: that is the ordinary case, and
          an empty line would claim the server said something it did not.

          A field error is already on the box above, so the banner would repeat
          it — hence the `fieldError === null` guard, matching how the mailer
          card on `/settings` splits the same two cases.
        */}
        {failure && failure.fieldError === null && (
          <Notice tone="danger" role="alert" title="Couldn’t start the support session.">
            <span className="min-w-0 [overflow-wrap:anywhere]">
              {failure.message}
              {failure.reasons.map((entry) => (
                <span key={entry} className="mt-1 block">
                  {entry}
                </span>
              ))}
              {failure.note !== null && <> {failure.note}</>}
            </span>
          </Notice>
        )}
      </div>
    </Modal>
  );
}
